// RecordAuditSeriesDefinition — per-branch audit number series, optionally one series per
// linked record type (T20-142255). Mirrors the workshop interest series definition.
import {z} from 'zod';

// Single source of the linkable record types; `RECORD_AUDIT_PAIR_RESOURCE_IDS` in `@dms/shared`
// derives from it, so the order here is the UI order.
export const recordAuditLinkableResourceIdSchema = z.enum([
  'BUSINESS_CASE',
  'SERVICE_CASE',
  'SERVICE_ORDER',
  'RENTAL_ORDER',
  'VEHICLE',
  'INTEREST',
  'WORKSHOP_INTEREST',
]);
export type RecordAuditLinkableResourceId = z.infer<typeof recordAuditLinkableResourceIdSchema>;

// The BE validates `resourceId` against the enum on requests only; responses type it as a string.
export const recordAuditRecordSeriesItemSchema = z.object({
  resourceId: recordAuditLinkableResourceIdSchema,
  seriesId: z.string(),
});
export type RecordAuditRecordSeriesItem = z.infer<typeof recordAuditRecordSeriesItemSchema>;

export const recordAuditRecordSeriesResponseBodySchema = z.object({
  resourceId: z.string(),
  seriesId: z.string(),
});

export const recordAuditSeriesDefinitionResponseBodySchema = z.object({
  id: z.string(),
  name: z.string(),
  branchId: z.string().nullable(),
  defaultSeriesId: z.string(),
  isSystem: z.boolean(),
  recordSeries: z.array(recordAuditRecordSeriesResponseBodySchema),
});
export type RecordAuditSeriesDefinitionResponseBody = z.infer<
  typeof recordAuditSeriesDefinitionResponseBodySchema
>;

export const getRecordAuditSeriesDefinitionListApiResponseSchema = z.array(
  recordAuditSeriesDefinitionResponseBodySchema
);
export type GetRecordAuditSeriesDefinitionListApiResponse =
  RecordAuditSeriesDefinitionResponseBody[];
export type GetRecordAuditSeriesDefinitionListApiArg = void;

export type GetRecordAuditSeriesDefinitionApiArg = {
  seriesDefinitionId: string;
};
export type GetRecordAuditSeriesDefinitionApiResponse = RecordAuditSeriesDefinitionResponseBody;

export const createRecordAuditSeriesDefinitionRequestBodySchema = z.object({
  name: z.string(),
  branchId: z.string(),
  defaultSeriesId: z.string(),
  recordSeries: z.array(recordAuditRecordSeriesItemSchema),
});
export type CreateRecordAuditSeriesDefinitionRequestBody = z.infer<
  typeof createRecordAuditSeriesDefinitionRequestBodySchema
>;

export type CreateRecordAuditSeriesDefinitionApiArg = {
  body: CreateRecordAuditSeriesDefinitionRequestBody;
};
export type CreateRecordAuditSeriesDefinitionApiResponse = /** status 201 */ {
  id: string;
};

// `branchId` may be null only for the system row (ignored there by the BE). `recordSeries` is a
// full replacement.
export const updateRecordAuditSeriesDefinitionRequestBodySchema = z.object({
  name: z.string(),
  branchId: z.string().nullable(),
  defaultSeriesId: z.string(),
  recordSeries: z.array(recordAuditRecordSeriesItemSchema),
});
export type UpdateRecordAuditSeriesDefinitionRequestBody = z.infer<
  typeof updateRecordAuditSeriesDefinitionRequestBodySchema
>;

export type UpdateRecordAuditSeriesDefinitionApiArg = {
  seriesDefinitionId: string;
  body: UpdateRecordAuditSeriesDefinitionRequestBody;
};
export type UpdateRecordAuditSeriesDefinitionApiResponse = /** status 204 */ undefined;
